import React from 'react';
import { Send, Clock, Save, Crown, AlertTriangle } from 'lucide-react';
import { useMonthlyPostsLimit, useScheduledQueueLimit } from '../../hooks/useSocialMediaLimits';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface PublishButtonProps {
  status: 'draft' | 'scheduled' | 'published';
  mode: 'create' | 'edit';
  isLoading: boolean;
  disabled?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit';
}

const PublishButton: React.FC<PublishButtonProps> = ({
  status,
  mode,
  isLoading,
  disabled = false,
  onClick,
  type = 'submit'
}) => {
  const monthlyLimit = useMonthlyPostsLimit();
  const queueLimit = useScheduledQueueLimit();

  // Determine if we can perform the action
  const canPublish = status === 'published' ? monthlyLimit.canPublish : true;
  const canSchedule = status === 'scheduled' ? queueLimit.canSchedule : true;
  const canPerformAction = canPublish && canSchedule;

  // Get limit info for tooltips
  const getPublishLimitInfo = () => {
    if (monthlyLimit.unlimited) return null;
    return {
      current: monthlyLimit.current_count || 0,
      limit: monthlyLimit.limit || 0,
      remaining: monthlyLimit.remaining || 0,
      isNearLimit: (monthlyLimit.current_count || 0) >= (monthlyLimit.limit || 0) * 0.8
    };
  };

  const getQueueLimitInfo = () => {
    if (queueLimit.unlimited) return null;
    return {
      current: queueLimit.current_count || 0,
      limit: queueLimit.limit || 0,
      remaining: queueLimit.remaining || 0,
      isNearLimit: (queueLimit.current_count || 0) >= (queueLimit.limit || 0) * 0.8
    };
  };

  const publishInfo = getPublishLimitInfo();
  const queueInfo = getQueueLimitInfo();

  // Button content based on status
  const getButtonContent = () => {
    if (isLoading) {
      return (
        <>
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
          <span>
            {status === 'published'
              ? 'Publishing...'
              : status === 'scheduled'
                ? 'Scheduling...'
                : mode === 'create' ? 'Creating...' : 'Saving...'
            }
          </span>
        </>
      );
    }

    if (status === 'published') {
      return (
        <>
          <Send className="w-4 h-4" />
          <span>{mode === 'create' ? 'Publish Now' : 'Update & Publish'}</span>
          {publishInfo && !canPublish && <Crown className="w-4 h-4 ml-1" />}
        </>
      );
    }

    if (status === 'scheduled') {
      return (
        <>
          <Clock className="w-4 h-4" />
          <span>{mode === 'create' ? 'Schedule Post' : 'Save Changes'}</span>
          {queueInfo && !canSchedule && <Crown className="w-4 h-4 ml-1" />}
        </>
      );
    }

    return (
      <>
        <Save className="w-4 h-4" />
        <span>{mode === 'create' ? 'Save as Draft' : 'Save Changes'}</span>
      </>
    );
  };

  // Tooltip content
  const getTooltipContent = () => {
    // Always show something for scheduled posts
    if (status === 'scheduled') {
      if (queueInfo) {
        if (queueInfo.unlimited) {
          return (
            <div className="text-center">
              <div className="font-medium text-green-400 mb-1">Unlimited Scheduling</div>
              <div className="text-sm">No queue limits on your plan</div>
            </div>
          );
        }
        if (!canSchedule) {
          return (
            <div className="text-center">
              <div className="font-medium text-red-400 mb-1">Queue Limit Reached</div>
              <div className="text-sm">{queueInfo.current_count}/{queueInfo.limit} scheduled posts</div>
            </div>
          );
        }
        return (
          <div className="text-center">
            <div className="font-medium mb-1">Scheduled Queue Usage</div>
            <div className="text-sm">{queueInfo.current_count}/{queueInfo.limit} posts scheduled</div>
          </div>
        );
      } else {
        return (
          <div className="text-center">
            <div className="font-medium mb-1">Loading Queue Status...</div>
            <div className="text-sm">Checking your scheduled posts limit</div>
          </div>
        );
      }
    }

    if (status === 'published' && publishInfo) {
      if (publishInfo.unlimited) {
        return (
          <div className="text-center">
            <div className="font-medium text-green-400 mb-1">Unlimited Publishing</div>
            <div className="text-sm">No monthly limits on your plan</div>
          </div>
        );
      }
      if (!canPublish) {
        return (
          <div className="text-center">
            <div className="font-medium text-red-400 mb-1">Monthly Content Limit Reached</div>
            <div className="text-sm">
              {publishInfo.current_count}/{publishInfo.limit} content pieces used
            </div>
            <div className="text-xs text-gray-300 mt-1">
              Upgrade your plan to publish more content
            </div>
          </div>
        );
      }
      if (publishInfo.isNearLimit) {
        return (
          <div className="text-center">
            <div className="font-medium text-yellow-400 mb-1">Approaching Monthly Limit</div>
            <div className="text-sm">
              {publishInfo.remaining} content pieces remaining
            </div>
          </div>
        );
      }
      return (
        <div className="text-center">
          <div className="font-medium mb-1">Monthly Content Usage</div>
          <div className="text-sm">
            {publishInfo.current_count}/{publishInfo.limit} content pieces used
          </div>
        </div>
      );
    }

    if (status === 'scheduled' && queueInfo) {
      if (!canSchedule) {
        return (
          <div className="text-center">
            <div className="font-medium text-red-400 mb-1">Queue Limit Reached</div>
            <div className="text-sm">
              {queueInfo.current}/{queueInfo.limit} scheduled posts
            </div>
            <div className="text-xs text-gray-300 mt-1">
              Upgrade your plan for more queue slots
            </div>
          </div>
        );
      }
      if (queueInfo.isNearLimit) {
        return (
          <div className="text-center">
            <div className="font-medium text-yellow-400 mb-1">Queue Almost Full</div>
            <div className="text-sm">
              {queueInfo.remaining} slots remaining
            </div>
          </div>
        );
      }
      return (
        <div className="text-center">
          <div className="font-medium mb-1">Scheduled Queue Usage</div>
          <div className="text-sm">
            {queueInfo.current}/{queueInfo.limit} posts scheduled
          </div>
        </div>
      );
    }

    return null;
  };

  const tooltipContent = getTooltipContent();
  const isDisabled = disabled || isLoading || !canPerformAction;

  // Debug logging
  console.log('PublishButton Debug:', {
    status,
    publishInfo,
    queueInfo,
    canPublish,
    canSchedule,
    tooltipContent: !!tooltipContent
  });

  const buttonElement = (
    <button
      id="publish-button"
      type={type}
      disabled={isDisabled}
      onClick={onClick}
      title=""
      className={`relative overflow-hidden px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg text-sm ${
        !canPerformAction
          ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/25'
          : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-500 hover:via-purple-500 hover:to-pink-500 text-white shadow-indigo-500/25'
      } ${
        isDisabled ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      {getButtonContent()}
    </button>
  );

  if (tooltipContent) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {buttonElement}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            {tooltipContent}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonElement;
};

export default PublishButton;